import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Search, CheckCircle2, XCircle } from 'lucide-react';
import { company, areas, cityData, citySlugs } from '../mock';
import Seo from '../components/Seo';

const slugForArea = (name) => citySlugs.find((s) => cityData[s].name === name);

// Zip codes Victory Plumbing services across Savannah & the Coastal Empire.
const SERVICED_ZIPS = new Set([
  '31401', '31404', '31405', '31406', '31407', '31408', '31409', '31410',
  '31411', '31415', '31416', '31419', '31420', '31421', // Savannah
  '31322', // Pooler
  '31324', // Richmond Hill
  '31328', // Tybee Island
  '31418', // Garden City
  '31302', // Bloomingdale
  '31326', // Rincon (surrounding)
  '31313', '31314', '31315', // Hinesville
]);

function ZipLookup() {
  const [zip, setZip] = useState('');
  const [result, setResult] = useState(null); // 'yes' | 'maybe' | 'invalid' | null

  const check = (e) => {
    e.preventDefault();
    const clean = zip.trim();
    if (!/^\d{5}$/.test(clean)) { setResult('invalid'); return; }
    setResult(SERVICED_ZIPS.has(clean) ? 'yes' : 'maybe');
  };

  return (
    <section className="zip-section" id="zip-check">
      <div className="wrap">
        <div className="zip-card" data-testid="zip-lookup">
          <p className="eyebrow" style={{ justifyContent: 'center' }}>Check Your Area</p>
          <h2>Do we service your zip code?</h2>
          <p className="zip-sub">Enter your 5-digit zip code and we&rsquo;ll confirm if you&rsquo;re in our service area.</p>
          <form className="zip-form" onSubmit={check}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              placeholder="e.g. 31401"
              value={zip}
              onChange={(e) => { setZip(e.target.value.replace(/[^\d]/g, '')); setResult(null); }}
              aria-label="Zip code"
              data-testid="zip-input"
            />
            <button className="btn btn-gold" type="submit" data-testid="zip-check-btn">
              <Search size={16} /> Check
            </button>
          </form>

          {result === 'yes' && (
            <div className="zip-result zip-yes" data-testid="zip-result">
              <CheckCircle2 size={20} />
              <span>Great news &mdash; we service <strong>{zip}</strong>! Give us a call or send a message to get started.</span>
            </div>
          )}
          {result === 'maybe' && (
            <div className="zip-result zip-maybe" data-testid="zip-result">
              <MapPin size={20} />
              <span>We may still be able to help near <strong>{zip}</strong>. Reach out and we&rsquo;ll confirm right away.</span>
            </div>
          )}
          {result === 'invalid' && (
            <div className="zip-result zip-invalid" data-testid="zip-result">
              <XCircle size={20} />
              <span>Please enter a valid 5-digit zip code.</span>
            </div>
          )}

          {(result === 'yes' || result === 'maybe') && (
            <div className="about-cta" style={{ justifyContent: 'center', marginTop: '20px' }}>
              <a className="btn btn-gold" href={`mailto:${company.email}?subject=Service%20Inquiry%20%E2%80%94%20Zip%20${zip}`}><Mail size={16} /> Get in Touch</a>
              <a className="btn btn-outline-dark" href={company.phoneHref}><Phone size={15} /> {company.phoneDisplay}</a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return (
    <main className="page services-page">
      <Seo title="Service Areas" description="Victory Plumbing serves Savannah, Pooler, Richmond Hill, Tybee Island, Garden City, Port Wentworth, Thunderbolt & Bloomingdale. Check your zip code." path="/service-areas" />
      <section className="page-hero">
        <div className="wrap">
          <p className="eyebrow">Service Areas</p>
          <h1>Serving Savannah &amp; the Coastal Empire</h1>
          <p className="lede">
            From the Historic District&rsquo;s century-old cast iron to new construction on the islands, our
            trucks are on the road across Chatham County and the surrounding communities every day.
          </p>
        </div>
      </section>

      {/* Areas We Serve + map */}
      <section className="areas-page" id="areas" style={{ scrollMarginTop: '120px' }}>
        <div className="wrap">
          <div className="areas-page-grid">
            <div>
              <p className="eyebrow">Where We Work</p>
              <h2>Areas we proudly serve</h2>
              <p className="lede">
                Family owned and operated in {company.location}, we serve homeowners and businesses across
                the Lowcountry. Don&rsquo;t see your town? Check your zip code below or give us a call.
              </p>
              <div className="area-chip-grid" data-testid="areas-chip-grid">
                {areas.map((a) => {
                  const s = slugForArea(a);
                  return s
                    ? <Link className="area-chip area-chip-link" key={a} to={`/service-areas/${s}`} data-testid={`area-link-${s}`}><MapPin size={15} />{a}</Link>
                    : <div className="area-chip" key={a}><MapPin size={15} />{a}</div>;
                })}
              </div>
              <div className="about-cta" style={{ marginTop: '26px' }}>
                <a className="btn btn-outline-dark" href={company.phoneHref}><Phone size={15} /> {company.phoneDisplay}</a>
              </div>
            </div>
            <div className="areas-page-map">
              <iframe
                title="Victory Plumbing service area map"
                src="https://www.google.com/maps?q=Savannah,+Georgia&z=10&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <ZipLookup />
    </main>
  );
}
